import React from "react";
import { useHistory } from "react-router-dom";
import { Button, ButtonVariant, Text, TextField } from "@merge-api/merge-javascript-shared";
import AuthCard from "../../shared/AuthCard";
import CountryFooterSelector from "../../shared/CountryFooterSelector";
import GoogleSSOButton from "../../shared/GoogleSSOButton";
import { LOGIN_PATH } from "../../../../../router/RouterUtils";
import { Tenancy, getTenancy } from "../../../../../api-client/APIClient";
import { signupTenantOptions } from "../../../../../constants";
import { SignupIntent } from "../../../../shared/utils/signupIntent";
import signupTenantOptionsWithIntent from "../utils/signupTenantOptionsWithIntent";

interface Props {
  email: string;
  setEmail: (value: string) => void;
  emailError?: string;
  setEmailError: (value: string | undefined) => void;
  onSubmit: () => void;
  isLoading: boolean;
  hideGoogleSSO: boolean;
  isExiting?: boolean;
  signupIntent?: SignupIntent;
}

const EmailStep = ({
  email,
  setEmail,
  emailError,
  setEmailError,
  onSubmit,
  isLoading,
  hideGoogleSSO,
  isExiting,
  signupIntent,
}: Props) => {
  const history = useHistory();
  const tenancy = getTenancy();
  const isSingleTenant = tenancy === Tenancy.SingleTenant;
  const tenantOption =
    signupTenantOptions[
      tenancy === Tenancy.EUMultiTenant ? "eu" : tenancy === Tenancy.APMultiTenant ? "apac" : "usa"
    ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  const handleSignInClick = (e: React.MouseEvent) => {
    e.preventDefault();
    history.push(LOGIN_PATH, { email });
  };

  return (
    <AuthCard
      className={`p-6 flex flex-col gap-6 ${isExiting ? "animate-slideOut" : "animate-slideDown"}`}
    >
      <Text variant="h2" as="h1">
        Get started for free
      </Text>

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
        <TextField
          name="email"
          type="email"
          labelTitle="Work email"
          placeholder="Email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError(undefined);
          }}
          error={!!emailError}
          errorText={emailError}
          autoFocus
        />
        <Button
          type="submit"
          fullWidth
          loading={isLoading}
          variant={ButtonVariant.PrimaryGray}
          id="gtag-signup-get-started"
        >
          Get started
        </Button>
      </form>

      {!hideGoogleSSO && (
        <GoogleSSOButton
          text="Sign up with Google"
          loginHint={email || undefined}
          disabled={isLoading}
        />
      )}

      <div className="flex items-center justify-between">
        <Text variant="md">
          Already have an account?{" "}
          {/* Raw <a> because we intercept with history.push to carry email in location.state (DX1) */}
          <a
            href={LOGIN_PATH}
            onClick={handleSignInClick}
            className="text-blue-60 underline hover:text-blue-80"
          >
            Sign in
          </a>
        </Text>
        {!isSingleTenant && (
          <CountryFooterSelector
            options={signupTenantOptionsWithIntent(signupIntent)}
            selectedOption={tenantOption}
          />
        )}
      </div>
    </AuthCard>
  );
};

export default EmailStep;
